import React from 'react';

// Loading placeholder/skeleton component
export const Placeholder = ({ 
  width = 'w-full', 
  height = 'h-4', 
  rounded = 'rounded', 
  className = '' 
}) => {
  return (
    <div className={`${width} ${height} ${rounded} bg-gray-200 animate-pulse ${className}`}>
      <div className="skeleton h-full w-full"></div>
    </div>
  );
};

// Text placeholder
export const TextPlaceholder = ({ lines = 1, className = '' }) => {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }, (_, index) => (
        <Placeholder
          key={index}
          width={index === lines - 1 ? 'w-3/4' : 'w-full'}
          height="h-4"
          className="mb-2 last:mb-0"
        />
      ))}
    </div>
  );
};

// Avatar placeholder
export const AvatarPlaceholder = ({ size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  return (
    <Placeholder
      width={sizeClasses[size]}
      height={sizeClasses[size]}
      rounded="rounded-full"
    />
  );
};

// Card placeholder
export const CardPlaceholder = ({ className = '' }) => {
  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
      <div className="space-y-4">
        <Placeholder width="w-3/4" height="h-6" />
        <TextPlaceholder lines={3} />
        <div className="flex space-x-2">
          <Placeholder width="w-20" height="h-8" rounded="rounded-md" />
          <Placeholder width="w-24" height="h-8" rounded="rounded-md" />
        </div>
      </div>
    </div>
  );
};

// Table row placeholder
export const TableRowPlaceholder = ({ columns = 6 }) => {
  return (
    <tr className="border-b border-gray-200">
      {Array.from({ length: columns }, (_, index) => (
        <td key={index} className="px-6 py-4">
          <Placeholder 
            width={index === 0 ? 'w-3/4' : 'w-full'} 
            height="h-4" 
          />
        </td>
      ))}
    </tr>
  );
};

// Button placeholder
export const ButtonPlaceholder = ({ 
  size = 'md', 
  variant = 'primary',
  className = '' 
}) => {
  const sizeClasses = {
    sm: 'h-8 w-20',
    md: 'h-10 w-24',
    lg: 'h-12 w-32'
  };

  return (
    <Placeholder
      width={sizeClasses[size]}
      height={sizeClasses[size]}
      rounded="rounded-lg"
      className={className}
    />
  );
};

// Chart placeholder
export const ChartPlaceholder = ({ 
  height = 'h-64', 
  className = '' 
}) => {
  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
      <div className="space-y-4">
        <Placeholder width="w-1/3" height="h-6" />
        <div className={`${height} bg-gray-100 rounded flex items-end justify-around p-4`}>
          {Array.from({ length: 8 }, (_, index) => (
            <div
              key={index}
              className="bg-gray-300 animate-pulse rounded-sm"
              style={{
                width: '20px',
                height: `${Math.random() * 80 + 20}%`
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Full page loading placeholder
export const PagePlaceholder = () => {
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <Placeholder width="w-1/4" height="h-8" />
        <Placeholder width="w-1/2" height="h-4" />
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Array.from({ length: 3 }, (_, index) => (
          <CardPlaceholder key={index} />
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <Placeholder width="w-1/4" height="h-6" />
        </div>
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              {Array.from({ length: 6 }, (_, index) => (
                <th key={index} className="px-6 py-3">
                  <Placeholder width="w-full" height="h-4" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }, (_, index) => (
              <TableRowPlaceholder key={index} columns={6} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Stat card placeholder
export const StatPlaceholder = ({ className = '' }) => {
  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
      <div className="space-y-3">
        <Placeholder width="w-1/2" height="h-4" />
        <Placeholder width="w-3/4" height="h-8" />
        <div className="flex items-center space-x-2">
          <Placeholder width="w-4" height="h-4" rounded="rounded-full" />
          <Placeholder width="w-1/3" height="h-3" />
        </div>
      </div>
    </div>
  );
};

export default Placeholder;